/**
 * Plan Status Badge Component - Clay-Inspired Premium UI
 */

import React from 'react';
import { Sparkles, Zap, Crown } from 'lucide-react';
import { motion } from 'framer-motion';

const buttonVariants = {
  tap: { scale: 0.95 },
  hover: { scale: 1.05, y: -2 }
};

export default function PlanStatusBadge({ userStatus, onClick, compact = false }) {
  if (!userStatus) return null;

  const { planType, remaining, isUnlimited, proDaysLeft } = userStatus;

  const getConfig = () => {
    switch (planType) {
      case 'pro':
        return {
          icon: Crown,
          label: 'Pro',
          sublabel: proDaysLeft ? `${proDaysLeft}d left` : 'Unlimited',
          color: 'var(--swoopy-pink)',
          bgColor: 'var(--swoopy-pink-soft)',
          gradient: 'linear-gradient(145deg, var(--swoopy-pink-soft), rgba(233, 116, 143, 0.2))'
        };
      case 'basic':
        return {
          icon: Zap,
          label: 'Basic',
          sublabel: `${remaining} left`,
          color: '#F59E0B',
          bgColor: 'rgba(255, 183, 77, 0.15)',
          gradient: 'linear-gradient(145deg, rgba(255, 183, 77, 0.15), rgba(255, 183, 77, 0.25))'
        };
      default:
        return {
          icon: Sparkles,
          label: 'Free',
          sublabel: `${remaining} left`,
          color: 'var(--text-secondary)',
          bgColor: 'var(--clay-white)',
          gradient: 'linear-gradient(145deg, var(--card-bg), var(--clay-white))'
        };
    }
  };

  const config = getConfig();
  const Icon = config.icon;

  if (compact) {
    return (
      <motion.button
        onClick={onClick}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full transition-all relative overflow-hidden"
        style={{ 
          background: config.gradient,
          boxShadow: 'var(--shadow-clay-sm)'
        }}
        variants={buttonVariants}
        whileTap="tap"
        whileHover="hover"
        data-testid="plan-status-badge"
      >
        {/* Inner highlight */}
        <div 
          className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
          style={{ 
            background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)'
          }}
        />
        <Icon size={15} style={{ color: config.color }} className="relative z-10" />
        <span 
          className="text-xs font-extrabold relative z-10"
          style={{ color: config.color }}
        >
          {isUnlimited ? 'Unlimited' : config.sublabel}
        </span>
      </motion.button>
    );
  }

  return (
    <motion.button
      onClick={onClick}
      className="flex items-center gap-3 px-5 py-3 rounded-2xl transition-all relative overflow-hidden"
      style={{ 
        background: config.gradient,
        boxShadow: 'var(--shadow-clay-md)'
      }}
      variants={buttonVariants}
      whileTap="tap"
      whileHover="hover"
      data-testid="plan-status-badge"
    >
      {/* Inner highlight */}
      <div 
        className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
        style={{ 
          background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      <div 
        className="w-10 h-10 rounded-xl flex items-center justify-center relative z-10"
        style={{ 
          backgroundColor: `${config.color}15`,
          boxShadow: 'var(--shadow-clay-xs)'
        }}
      >
        <Icon size={20} style={{ color: config.color }} />
      </div>
      <div className="text-left relative z-10">
        <p 
          className="text-xs font-bold"
          style={{ color: 'var(--text-muted)' }}
        >
          {config.label} Plan
        </p>
        <p 
          className="text-sm font-extrabold"
          style={{ color: config.color }}
        >
          {isUnlimited ? 'Unlimited' : config.sublabel}
        </p>
      </div>
    </motion.button>
  );
}
